// src/components/package/PaymentListLayer.jsx

import React, { useEffect, useMemo, useState } from "react";
import { 
  FaSearch, 
  FaSchool, 
  FaFilter, 
  FaChevronLeft, 
  FaChevronRight, 
  FaDatabase,
  FaCheckCircle,
  FaTimesCircle,
  FaCalendarAlt,
  FaUsers
} from "react-icons/fa";
import { toast } from "react-toastify";
import { getReports } from "../../services/reportsSchoolService";

export const PaymentListLayer = () => {
  const [loading, setLoading] = useState(false);
  const [metrics, setMetrics] = useState({});
  const [schools, setSchools] = useState([]);
  const [packageBreakdown, setPackageBreakdown] = useState([]);

  // Filters & Pagination States
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [page, setPage] = useState(1);
  const pageSize = 8; // Items per page

  const fetchReports = async () => {
    try {
      setLoading(true);
      const res = await getReports();

      if (res?.status) {
        setMetrics(res.metrics || {});
        setSchools(res.schools || []);
        setPackageBreakdown(res.package_breakdown || []);
      } else {
        toast.error(res?.message || "Failed to load reports");
      }
    } catch (error) {
      toast.error("Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReports();
  }, []);

  /* ================= CLIENT-SIDE FILTER LOGIC ================= */
  const filteredSchools = useMemo(() => {
    return schools.filter((school) => {
      const matchesSearch = 
        school.school_name?.toLowerCase().includes(search.toLowerCase()) ||
        school.email?.toLowerCase().includes(search.toLowerCase()) ||
        school.phone?.includes(search);

      const currentStatus = school.subscription_status?.toLowerCase() || "none";
      const matchesStatus = 
        statusFilter === "all" ? true : currentStatus === statusFilter;

      return matchesSearch && matchesStatus;
    });
  }, [schools, search, statusFilter]);

  // Page resetting when filters change
  useEffect(() => {
    setPage(1);
  }, [search, statusFilter]);

  /* ================= PAGINATION CALCULATIONS ================= */
  const totalPages = Math.ceil(filteredSchools.length / pageSize);
  const displayedSchools = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filteredSchools.slice(start, start + pageSize);
  }, [filteredSchools, page]);

  return (
    <div className="container-fluid py-4" style={{ backgroundColor: "#F8FAFC", minHeight: "100vh", fontFamily: "'Inter', sans-serif" }}>
      
      {/* HEADER SECTION */}
      <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center mb-4 gap-3">
        <div>
          <h4 className="fw-bold text-dark mb-1 animate__animated animate__fadeInDown" style={{ letterSpacing: "-0.5px" }}>School Package Reports</h4>
          <p className="text-muted small mb-0">Monitor and analyze school subscriptions and analytical insights</p>
        </div>
        <button 
          className="btn btn-white border shadow-sm text-secondary d-flex align-items-center gap-2 px-3 py-2"
          onClick={fetchReports}
          disabled={loading}
          style={{ borderRadius: "10px", background: "#fff", transition: "all 0.2s" }}
        >
          <FaDatabase size={13} className={loading ? "spinner-border spinner-border-sm border-0" : ""} />
          Refresh Data
        </button>
      </div>

      {/* METRICS METERS / CARDS */}
      <div 
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
          gap: "20px",
          marginBottom: "30px",
        }}
      >
        {[
          { label: "Total Schools", value: metrics.total_schools || 0, icon: <FaSchool size={20} />, text: "#4F46E5", iconBg: "#EEF2FF" },
          { label: "Active Schools", value: metrics.active_schools || 0, icon: <FaCheckCircle size={20} />, text: "#10B981", iconBg: "#ECFDF5" },
          { label: "Inactive Schools", value: metrics.inactive_schools || 0, icon: <FaTimesCircle size={20} />, text: "#64748B", iconBg: "#F8FAFC" },
          { label: "Active Plans", value: metrics.active_subscriptions || 0, icon: <FaCalendarAlt size={20} />, text: "#06B6D4", iconBg: "#ECFEFF" },
          { label: "Expired Plans", value: metrics.expired_subscriptions || 0, icon: <FaTimesCircle size={20} />, text: "#EF4444", iconBg: "#FFF1F2" },
        ].map((card, idx) => (
          <div 
            key={idx} 
            className="p-4 border-0 shadow-sm position-relative overflow-hidden" 
            style={{ 
              borderRadius: "16px", 
              background: "#fff",
              transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
              cursor: "pointer",
              borderLeft: `4px solid ${card.text}`
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = "translateY(-4px)";
              e.currentTarget.style.boxShadow = "0 10px 15px -3px rgba(0, 0, 0, 0.1)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "translateY(0)";
              e.currentTarget.style.boxShadow = "0 4px 6px -1px rgba(0, 0, 0, 0.05)";
            }}
          >
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <p className="text-uppercase text-muted fw-bold small mb-1" style={{ letterSpacing: "0.5px", fontSize: "11px" }}>{card.label}</p>
                <h2 className="fw-extrabold mb-0" style={{ color: card.text, fontSize: "32px" }}>{card.value}</h2>
              </div>
              <div className="p-3 rounded-3" style={{ background: card.iconBg, color: card.text }}>
                {card.icon}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* FILTERS & CONTROLS SECTION */}
      <div className="bg-white p-3 border-0 shadow-sm mb-4 d-flex flex-column flex-md-row gap-3 justify-content-between align-items-stretch align-items-md-center" style={{ borderRadius: "16px" }}>
        
        {/* Search Input Container */}
        <div className="position-relative flex-grow-1" style={{ maxWidth: "450px" }}>
          <FaSearch className="position-absolute top-50 start-0 translate-middle-y ms-3 text-muted" />
          <input
            type="text"
            className="form-control ps-5 py-2.5 border-0"
            placeholder="Search school name, email or phone..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ borderRadius: "12px", background: "#F1F5F9", fontSize: "14px", fontWeight: "500" }}
          />
        </div>

        {/* Filters Group Dropdown */}
        <div className="d-flex align-items-center gap-2">
          <FaFilter className="text-muted small" />
          <select
            className="form-select border-0 py-2.5 px-3"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            style={{ borderRadius: "12px", background: "#F1F5F9", fontSize: "14px", fontWeight: "500", minWidth: "160px", cursor: "pointer" }}
          >
            <option value="all">All Statuses</option>
            <option value="active">Active Plan</option>
            <option value="expired">Expired Plan</option>
          </select>
        </div>

      </div>

      {/* BOTTOM LAYOUT GRID (Package Summary + Detailed Report Table) */}
      <div className="row g-4">
        
        {/* LEFT COLUMN: Package Summary Analytics (100% Width on desktop) */}
        <div className="col-12">
          <div className="bg-white border-0 shadow-sm" style={{ borderRadius: "16px", overflow: "hidden" }}>
            <div className="px-4 py-3 bg-light border-bottom border-light-subtle">
              <h6 className="fw-bold text-dark mb-0 d-flex align-items-center gap-2">
                <FaUsers className="text-primary" /> Package Breakdown
              </h6>
            </div>
            <div className="p-4">
              <div className="row g-3">
                {packageBreakdown.length === 0 ? (
                  <div className="col-12 text-center text-muted py-3">No packages active</div>
                ) : (
                  packageBreakdown.map((item, index) => {
                    const nameLower = item.package_name?.toLowerCase() || "";
                    let themeBg = "#EFF6FF";
                    let themeColor = "#2563EB";
                    let iconBg = "#DBEAFE";
                    
                    if (nameLower.includes("new")) {
                      themeBg = "#F0FDF4"; // Green
                      themeColor = "#16A34A";
                      iconBg = "#DCFCE7";
                    } else if (nameLower.includes("today")) {
                      themeBg = "#FFFBEB"; // Gold
                      themeColor = "#D97706";
                      iconBg = "#FEF3C7";
                    } else if (nameLower.includes("one day")) {
                      themeBg = "#FAF5FF"; // Purple
                      themeColor = "#7C3AED";
                      iconBg = "#F3E8FF";
                    }
                    
                    return (
                      <div key={index} className="col-12 col-sm-6 col-md-4 col-lg-3">
                        <div 
                          className="p-3 border-0 rounded-4 d-flex align-items-center justify-content-between" 
                          style={{ 
                            background: themeBg, 
                            transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                            cursor: "pointer",
                            borderLeft: `4px solid ${themeColor}`,
                            boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.05)"
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.transform = "translateY(-3px)";
                            e.currentTarget.style.boxShadow = "0 8px 12px -3px rgba(0, 0, 0, 0.08)";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.transform = "translateY(0)";
                            e.currentTarget.style.boxShadow = "0 4px 6px -1px rgba(0, 0, 0, 0.05)";
                          }}
                        >
                          <div>
                            <span className="text-muted small fw-bold text-uppercase" style={{ letterSpacing: "0.5px", fontSize: "11px" }}>{item.package_name}</span>
                            <h4 className="fw-extrabold mb-0 mt-1" style={{ color: themeColor, fontSize: "24px" }}>{item.school_count}</h4>
                          </div>
                          <div className="p-2.5 rounded-3" style={{ background: iconBg, color: themeColor }}>
                            <FaUsers size={18} />
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          </div>
        </div>

        {/* RIGHT COLUMN: Master Detailed Schools Report Table (100% Width on desktop) */}
        <div className="col-12">
          <div className="bg-white border-0 shadow-sm" style={{ borderRadius: "16px", overflow: "hidden" }}>
            <div className="px-4 py-3 bg-light border-bottom border-light-subtle d-flex justify-content-between align-items-center">
              <h6 className="fw-bold text-dark mb-0">All Registered Institutions</h6>
              <span className="badge bg-primary-subtle text-primary rounded-pill px-3 py-1.5 fw-semibold" style={{ fontSize: "12px" }}>
                {filteredSchools.length} Total
              </span>
            </div>
            
            <div className="table-responsive">
              <table className="table table-hover align-middle mb-0" style={{ fontSize: "14px" }}>
                <thead className="table-light">
                  <tr>
                    <th className="ps-4 py-3 text-muted fw-bold text-uppercase" style={{ fontSize: "11px", letterSpacing: "0.5px" }}>ID</th>
                    <th className="py-3 text-muted fw-bold text-uppercase" style={{ fontSize: "11px", letterSpacing: "0.5px" }}>School Info</th>
                    <th className="py-3 text-muted fw-bold text-uppercase" style={{ fontSize: "11px", letterSpacing: "0.5px" }}>Contact</th>
                    <th className="py-3 text-muted fw-bold text-uppercase" style={{ fontSize: "11px", letterSpacing: "0.5px" }}>Package details</th>
                    <th className="py-3 text-muted fw-bold text-uppercase" style={{ fontSize: "11px", letterSpacing: "0.5px" }}>Limit Usage</th>
                    <th className="py-3 text-muted fw-bold text-uppercase" style={{ fontSize: "11px", letterSpacing: "0.5px" }}>Status</th>
                    <th className="pe-4 py-3 text-muted fw-bold text-uppercase" style={{ fontSize: "11px", letterSpacing: "0.5px" }}>Expiry Date</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan="7" className="text-center text-muted py-5">
                        <div className="spinner-border text-primary spinner-border-sm me-2" role="status"></div>
                        Fetching reports...
                      </td>
                    </tr>
                  ) : displayedSchools.length === 0 ? (
                    <tr>
                      <td colSpan="7" className="text-center text-muted py-5 fw-medium">
                        No Matching Records Found
                      </td>
                    </tr>
                  ) : (
                    displayedSchools.map((school) => {
                      const total = school.total_students || 0;
                      const limit = school.student_limit || 0;
                      const percent = limit > 0 ? Math.min(Math.round((total / limit) * 100), 100) : 0;
                      
                      let progressBg = "#10B981"; // green
                      if (percent >= 90) {
                        progressBg = "#EF4444"; // red
                      } else if (percent >= 70) {
                        progressBg = "#F59E0B"; // orange
                      }
                      
                      const isActive = school.subscription_status === "active";
                      
                      return (
                        <tr key={school.id} style={{ transition: "all 0.15s ease" }}>
                          <td className="ps-4 py-3.5 text-muted small fw-semibold">#{school.id}</td>
                          <td className="py-3.5">
                            <div className="d-flex align-items-center gap-3">
                              <div 
                                className="d-flex align-items-center justify-content-center text-white fw-bold rounded-circle shadow-sm"
                                style={{ 
                                  width: "36px", 
                                  height: "36px", 
                                  background: "linear-gradient(135deg, #4F46E5, #3B82F6)",
                                  fontSize: "14px",
                                  flexShrink: 0
                                }}
                              >
                                {school.school_name ? school.school_name.charAt(0).toUpperCase() : "S"}
                              </div>
                              <div>
                                <div className="fw-bold text-dark" style={{ fontSize: "14px" }}>{school.school_name}</div>
                                <div className="text-muted small" style={{ fontSize: "11px" }}>{school.email}</div>
                              </div>
                            </div>
                          </td>
                          <td className="py-3.5 text-secondary fw-semibold">{school.phone || "N/A"}</td>
                          <td className="py-3.5">
                            <span className="badge bg-primary-subtle text-primary border border-primary-subtle px-2.5 py-1.5 rounded-3 fw-semibold">
                              {school.package_name || "No Plan"}
                            </span>
                          </td>
                          <td className="py-3.5">
                            <div className="d-flex justify-content-between align-items-center mb-1.5" style={{ maxWidth: "150px" }}>
                              <span className="fw-bold text-dark" style={{ fontSize: "13px" }}>{total}</span>
                              <span className="text-muted small" style={{ fontSize: "11px" }}>/ {limit || "∞"}</span>
                            </div>
                            {limit > 0 && (
                              <div className="progress animate__animated animate__fadeIn" style={{ height: "6px", width: "150px", borderRadius: "3px", background: "#E2E8F0" }}>
                                <div 
                                  className="progress-bar" 
                                  style={{ 
                                    width: `${percent}%`, 
                                    backgroundColor: progressBg, 
                                    borderRadius: "3px",
                                    transition: "width 0.5s ease" 
                                  }}
                                />
                              </div>
                            )}
                          </td>
                          <td className="py-3.5">
                            <span 
                              className="badge d-inline-flex align-items-center gap-2 px-2.5 py-1.5 fw-semibold"
                              style={{
                                borderRadius: "30px",
                                backgroundColor: isActive ? "#DCFCE7" : "#FEE2E2",
                                color: isActive ? "#15803D" : "#B91C1C",
                                fontSize: "12px",
                                border: `1px solid ${isActive ? "#bbf7d0" : "#fecaca"}`
                              }}
                            >
                              <span 
                                style={{ 
                                  width: "6px", 
                                  height: "6px", 
                                  borderRadius: "50%", 
                                  backgroundColor: isActive ? "#16A34A" : "#EF4444",
                                  display: "inline-block" 
                                }} 
                              />
                              {school.subscription_status || "Inactive"}
                            </span>
                          </td>
                          <td className="pe-4 py-3.5 text-secondary small fw-medium">
                            {school.plan_expires_at || "—"}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
  
            {/* PREMIUM PAGINATION FOOTER */}
            {totalPages > 1 && (
              <div className="px-4 py-3 border-top border-light-subtle d-flex flex-column flex-sm-row justify-content-between align-items-center gap-3 bg-light-subtle">
                <span className="text-muted small">
                  Showing <strong className="text-dark">{displayedSchools.length}</strong> of{" "}
                  <strong className="text-dark">{filteredSchools.length}</strong> Institutions
                </span>
                
                <div className="d-flex gap-1">
                  <button
                    disabled={page === 1}
                    className="btn btn-sm btn-white border border-light-subtle d-flex align-items-center px-3"
                    onClick={() => setPage(page - 1)}
                    style={{ background: "#fff", borderRadius: "8px" }}
                  >
                    <FaChevronLeft size={10} className="me-1" /> Prev
                  </button>
                  
                  <div className="d-none d-sm-flex align-items-center px-3 text-muted small">
                    Page <strong>{page}</strong> of {totalPages}
                  </div>
  
                  <button
                    disabled={page === totalPages}
                    className="btn btn-sm btn-white border border-light-subtle d-flex align-items-center px-3"
                    onClick={() => setPage(page + 1)}
                    style={{ background: "#fff", borderRadius: "8px" }}
                  >
                    Next <FaChevronRight size={10} className="ms-1" />
                  </button>
                </div>
              </div>
            )}
  
          </div>
        </div>

      </div>
    </div>
  );
};